/**
 * Copyright (c) 2018, salesforce.com, inc.
 * All rights reserved.
 * Licensed under the BSD 3-Clause license.
 * For full license text, see LICENSE.txt file in the repo root or
 * https://opensource.org/licenses/BSD-3-Clause
 */
import React from "react";
import styled from "styled-components";
import _ from 'lodash'
import LocalePickerModal from "../../../LocalePickerModal";
import { UtilityIcon } from "../../../../components/SVGIcon";
import Localized from "../../../../../shared/components/Localized";

const Header = styled.div`
  color: #54698d;
  text-transform: uppercase;
  letter-spacing: 0.875px;
  align-items: center;
  font-size: 12px;
  background-color: #e6ecf2;
  padding: 7px 0 7px 1rem;
  width: 100%;
  box-shadow: 0 1px 0 0 #dddbda;
  @media (max-width: 767px) {
    display: none;
  }
`;

const Container = styled.div`
  min-height: 600px;
`;

const EmailContainer = styled.div`
  margin-bottom: 32px;
`;

const InfoHeading = styled.div`
  margin-top: 18px;
  font-size: 13px;
  color: #706e6b;
  padding-left: 1rem;
  @media (max-width: 767px) {
    font-size: 16px;
    line-height: 1.13;
    letter-spacing: normal;
    margin-bottom: 12px;
  }
`;

const SettingValue = styled.span`
  padding-left: 1rem;
  font-size: 12px;
  color: #2b2826;
  word-break: break-all;
  @media (max-width: 767px) {
    font-size: 18px;
    line-height: 1;
    letter-spacing: normal;
    vertical-align: bottom;
  }
  @media (min-width: 768px) {
    display: block;
  }
`;

const Verified = styled.span`
  font-size: 12px;
  line-height: 1.67;
  letter-spacing: normal;
  color: #2ead5d;
  padding-left: 1rem;
  @media (max-width: 767px) {
    font-size: 16px;
    line-height: 1.25;
  }
  @media (min-width: 768px) {
    display: block;
  }
`;

const NotVerified = styled.span`
  font-size: 12px;
  line-height: 1.67;
  color: #b0adab;
  padding-left: 1rem;
  vertical-align: middle;
  @media (max-width: 767px) {
    font-size: 16px;
    line-height: 1.25;
  }
  @media (min-width: 768px) {
    display: block;
  }
`;

const MobileContainer = styled.div``;

const LanguageContainer = styled.div`
  margin-top: 32px;
`;

const ActionButton = styled.span`
  margin-top: 12px;
  font-size: 13px;
  line-height: 1.54;
  color: #006dcc;
  padding-left: 1rem;
  cursor: pointer;
  margin-top: 6px;
  @media (max-width: 767px) {
    font-size: 18px;
    line-height: 1.11;
  }
`;

const ActionButtonContainer = styled.div`
  margin-top: 8px;
`;

class SubscriptionsSidePanel extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      languageModalOpen: false
    };

    this.onEditLanguageClose = this.onEditLanguageClose.bind(this);
    this.onEditLanguageClick = this.onEditLanguageClick.bind(this);
  }

  onEditLanguageClose(locale) {
    this.setState({ languageModalOpen: false });
    if(typeof locale === 'string' || locale instanceof String){
      this.props.onLocaleChange(locale);
    }
  }

  onEditLanguageClick() {
    this.setState({ languageModalOpen: true });
  }

  render() {
    const {
      heading,
      email,
      locales,
      selectedLocale,
      phone,
      emailVerified,
      mobileNumberVerified,
      onPhoneNumberAdd,
      onPhoneNumberRemove,
      onPhoneNumberVerify
    } = this.props;

    const selectedLanguage = _.find(locales, {
      value: selectedLocale
    }).text;

    const hasPhone = Boolean(phone);

    return (
      <Container>
        <Header>{heading}</Header>
        <EmailContainer>
          <InfoHeading>
            <Localized
              modelKey="emailAddress"
              modelAttribute="label"
              modelName="login"
            />
          </InfoHeading>
          <SettingValue>{email}</SettingValue>
          {emailVerified && (
            <Verified>
              <Localized
                modelKey="Verified"
                modelAttribute="label"
                modelName="subscriptions"
              />
            </Verified>
          )}
        </EmailContainer>
        <InfoHeading>
          <Localized
            modelKey="mobile"
            modelAttribute="label"
            modelName="subscriptions"
          />
        </InfoHeading>
        {hasPhone && (
          <MobileContainer>
            <SettingValue>{phone}</SettingValue>
            {mobileNumberVerified && (
              <Verified>
                <Localized
                  modelKey="Verified"
                  modelAttribute="label"
                  modelName="subscriptions"
                />
              </Verified>
            )}
            {!mobileNumberVerified && (
              <NotVerified>
                <UtilityIcon icon="warning" fill="#b0adab" />
                <span style={{ paddingLeft: "4px" }}>
                  <Localized
                    modelKey="notVerified"
                    modelAttribute="label"
                    modelName="subscriptions"
                  />
                </span>
              </NotVerified>
            )}
            <ActionButtonContainer>
              {!mobileNumberVerified && (
                <ActionButton onClick={onPhoneNumberVerify}>
                  <Localized
                    modelKey="verify"
                    modelAttribute="label"
                    modelName="subscriptions"
                  />
                </ActionButton>
              )}
              <ActionButton onClick={onPhoneNumberRemove}>
                <Localized
                  modelKey="remove"
                  modelAttribute="label"
                  modelName="subscriptions"
                />
              </ActionButton>
            </ActionButtonContainer>
          </MobileContainer>
        )}
        {!hasPhone && (
          <MobileContainer>
            <SettingValue>
              <Localized
                modelKey="noPhone"
                modelAttribute="label"
                modelName="subscriptions"
              />
            </SettingValue>
            <ActionButtonContainer>
              <ActionButton onClick={onPhoneNumberAdd}>
                <Localized
                  modelKey="add"
                  modelAttribute="label"
                  modelName="subscriptions"
                />
              </ActionButton>
            </ActionButtonContainer>
          </MobileContainer>
        )}
        <LanguageContainer>
          <InfoHeading>
            <Localized
              modelKey="Language"
              modelAttribute="header"
              modelName="metrics"
            />
          </InfoHeading>
          <SettingValue>{selectedLanguage}</SettingValue>
          <ActionButtonContainer>
            <ActionButton>
              <span onClick={this.onEditLanguageClick}>
                <Localized
                  modelKey="edit"
                  modelAttribute="label"
                  modelName="subscriptions"
                />
              </span>
            </ActionButton>
          </ActionButtonContainer>
          {this.state.languageModalOpen && (
            <LocalePickerModal onClose={this.onEditLanguageClose} />
          )}
        </LanguageContainer>
      </Container>
    );
  }
}

export default SubscriptionsSidePanel;
